import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { examAPI } from '../services/api';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { ExamDetailCard } from '../components/exam/ExamDetailCard';
import { examDetail } from '../utils/examDetail';

interface ExamSchedule {
  id: string;
  subject: string;
  examName: string;
  date: string;
  startTime: string;
  endTime: string;
  room: string;
}

import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { RootStackParamList } from '../types';

export function ExamScheduleScreen() {
  const route = useRoute<RouteProp<RootStackParamList, 'ExamSchedule'>>();
  const navigation = useNavigation();
  const { schedule, initialExamId, focus } = route.params || { schedule: [], initialExamId: null, focus: undefined };
  // Opened from an exam alert: that paper's details first (utils/examDetail).
  const detail = useMemo(() => examDetail(schedule, focus), [schedule, focus]);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [selectedExamId, setSelectedExamId] = useState<string | null>(
    initialExamId || (schedule && schedule.length > 0 ? (schedule[0].examId ?? schedule[0].ExamId ?? schedule[0].id) : null)
  );

  const selectedExamData = useMemo(() => {
    if (!schedule || !Array.isArray(schedule)) return [];
    const exam = schedule.find((e: any) => (e.examId ?? e.ExamId ?? e.id) === selectedExamId) || schedule[0];
    return exam?.classSchedules?.[0]?.subjectTimetable || exam?.ClassSchedules?.[0]?.SubjectTimetable || exam?.classSchedules?.[0]?.subjects || exam?.ClassSchedules?.[0]?.Subjects || [];
  }, [schedule, selectedExamId]);

  return (
    <View style={styles.modalOverlay}>
      <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => navigation.goBack()} />
      <View style={[styles.modalContent, isMobile && styles.modalContentMobile]}>
        <View style={styles.screenHeader}>
          <Text style={styles.title}>{detail ? 'Exam details' : 'Exam Schedule'}</Text>
          <TouchableOpacity style={styles.closeButton} onPress={() => navigation.goBack()}>
            <FeatherIcon name="x" size={24} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {!schedule || schedule.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No exams scheduled</Text>
          </View>
        ) : (
          <View style={{ flexGrow: 1 }}>
            {detail && <View style={{ marginHorizontal: -16, marginTop: 4 }}><ExamDetailCard detail={detail} /></View>}
            <View style={styles.tabsContainer}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsScroll}>
                {schedule.map((exam: any, index: number) => {
                  const currentExamId = exam.examId ?? exam.ExamId ?? exam.id;
                  const examName = exam.name ?? exam.Name ?? exam.examName ?? exam.ExamName ?? exam.examTypeName ?? 'Exam';
                  const isSelected = currentExamId === selectedExamId;
                  return (
                    <TouchableOpacity
                      key={currentExamId || String(index)}
                      style={[styles.tabButton, isSelected && styles.tabButtonActive]}
                      onPress={() => setSelectedExamId(currentExamId)}
                    >
                      <Text style={[styles.tabText, isSelected && styles.tabTextActive]}>
                        {examName}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
            {isMobile ? (
              <View style={styles.table}>
                <ScrollView style={{ maxHeight: 460 }}>
                  {selectedExamData.map((item: any, index: number) => (
                    <View key={item.subjectId || item.SubjectId || String(index)} style={[styles.examCardMobile, index % 2 === 0 && styles.tableRowAlt]}>
                      <Text style={[styles.tableCell, styles.bold, { marginBottom: 6 }]}>
                        {item.subjectName ?? item.SubjectName ?? item.name ?? item.Name}
                      </Text>
                      <View style={styles.examCardRowMobile}>
                        <Text style={styles.examCardLabelMobile}>Date</Text>
                        <Text style={styles.tableCell}>
                          {item.examDate ?? item.ExamDate ?? item.date ?? item.Date ? new Date(item.examDate ?? item.ExamDate ?? item.date ?? item.Date).toLocaleDateString() : '-'}
                        </Text>
                      </View>
                      <View style={styles.examCardRowMobile}>
                        <Text style={styles.examCardLabelMobile}>Time</Text>
                        <Text style={styles.tableCell}>
                          {(item.startTime ?? item.StartTime)?.substring(0, 5)} - {(item.endTime ?? item.EndTime)?.substring(0, 5)}
                        </Text>
                      </View>
                      <View style={styles.examCardRowMobile}>
                        <Text style={styles.examCardLabelMobile}>Max Marks</Text>
                        <Text style={styles.tableCell}>
                          {item.maxMarks ?? item.MaxMarks ?? '-'}
                        </Text>
                      </View>
                    </View>
                  ))}
                </ScrollView>
              </View>
            ) : (
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderCell, styles.subjectCol]}>Subject</Text>
                  <Text style={[styles.tableHeaderCell, styles.dateCol]}>Date</Text>
                  <Text style={[styles.tableHeaderCell, styles.timeCol]}>Time</Text>
                  <Text style={[styles.tableHeaderCell, styles.roomCol]}>Max Marks</Text>
                </View>
                <ScrollView style={{ maxHeight: 400 }}>
                  {selectedExamData.map((item: any, index: number) => (
                    <View key={item.subjectId || item.SubjectId || String(index)} style={[styles.tableRow, index % 2 === 0 && styles.tableRowAlt]}>
                      <Text style={[styles.tableCell, styles.subjectCol, styles.bold]}>
                        {item.subjectName ?? item.SubjectName ?? item.name ?? item.Name}
                      </Text>
                      <Text style={[styles.tableCell, styles.dateCol]}>
                        {item.examDate ?? item.ExamDate ?? item.date ?? item.Date ? new Date(item.examDate ?? item.ExamDate ?? item.date ?? item.Date).toLocaleDateString() : '-'}
                      </Text>
                      <Text style={[styles.tableCell, styles.timeCol]}>
                        {(item.startTime ?? item.StartTime)?.substring(0, 5)} - {(item.endTime ?? item.EndTime)?.substring(0, 5)}
                      </Text>
                      <Text style={[styles.tableCell, styles.roomCol]}>
                        {item.maxMarks ?? item.MaxMarks ?? '-'}
                      </Text>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    width: '100%',
    maxWidth: 800,
    maxHeight: '90%',
    padding: 24,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 10,
  },
  modalContentMobile: {
    padding: 14,
    borderRadius: 14,
    maxHeight: '95%',
  },
  examCardMobile: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  examCardRowMobile: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  examCardLabelMobile: {
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '600',
  },
  title: { fontSize: 24, fontWeight: '800', color: colors.textPrimary, marginBottom: 0 },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, fontWeight: '600' },
  table: { backgroundColor: colors.card, borderRadius: 12, overflow: 'hidden', elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  tableHeader: { flexDirection: 'row', backgroundColor: colors.primary, paddingVertical: 12, paddingHorizontal: 12 },
  tableRow: { flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: colors.border },
  tableRowAlt: { backgroundColor: colors.background },
  tableCell: { fontSize: 13, color: colors.textPrimary, fontWeight: '500' },
  tableHeaderCell: { fontSize: 13, color: '#FFF', fontWeight: '700' },
  subjectCol: { flex: 2 },
  dateCol: { flex: 1.5 },
  timeCol: { flex: 1.5 },
  roomCol: { flex: 1 },
  bold: { fontWeight: '700' },

  screenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabsContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tabsScroll: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: colors.divider,
    marginRight: 8,
  },
  tabButtonActive: {
    backgroundColor: colors.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  tabTextActive: {
    color: '#FFFFFF',
  },
});